import { useSyncExternalStore } from "react";
import type { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import { isBusyClosesDemoId } from "./busyClosesDemoIds";
import type { DemoRunRecord } from "./busyClosesDemo.types";
import {
    getBusyClosesDemoState,
    subscribeBusyClosesDemoStore,
} from "./busyClosesDemoStore";

const findRun = (isMatch: (run: AgentRunSummary) => boolean) =>
    [...getBusyClosesDemoState().runsById.values()].find(({ run }) =>
        isMatch(run),
    )?.run ?? null;

const useDemoSnapshot = <T>(getSnapshot: () => T): T =>
    useSyncExternalStore(subscribeBusyClosesDemoStore, getSnapshot);

/** The live fake run on a demo task (by its run task id); null for any real id. */
export const useBusyClosesDemoRunForTask = (
    taskId: string,
): AgentRunSummary | null =>
    useDemoSnapshot(() =>
        isBusyClosesDemoId(taskId)
            ? findRun((run) => run.runTaskId === taskId)
            : null,
    );

/** The live fake run on a demo close (raw close uuid); null for any real id. */
export const useBusyClosesDemoRunForClose = (
    closeId: string | null,
): AgentRunSummary | null =>
    useDemoSnapshot(() =>
        closeId !== null && isBusyClosesDemoId(closeId)
            ? findRun((run) => run.close?.id === closeId)
            : null,
    );

/** A fake run's live history (newest first) and run task title. */
export const useBusyClosesDemoRunRecord = (
    runId: string,
): DemoRunRecord | null =>
    useDemoSnapshot(() => getBusyClosesDemoState().runsById.get(runId) ?? null);

/** Who is viewing a demo close or task now; null for any real target, which keeps live presence. */
export const useBusyClosesDemoViewerIds = (
    targetId: string | null,
): number[] | null =>
    useDemoSnapshot(() =>
        targetId !== null && isBusyClosesDemoId(targetId)
            ? (getBusyClosesDemoState().viewersByTargetId.get(targetId)
                  ?.viewerIds ?? null)
            : null,
    );
